// 技選択・並び替えなどのUI外ロジックを集約
import type { PokemonV2 } from '../pokemon/pokemon';
import type { Move } from '../move/move';
import {
  MOVE_DAMAGE_CLASS_PHYSICAL,
  MOVE_DAMAGE_CLASS_SPECIAL,
  MOVE_DAMAGE_CLASS_STATUS,
} from '../move/move';

// よく使われる攻撃技(ID)を抽出（変化技は除外）
export function getOftenUsedAttackMoveIds(pokemon: PokemonV2, allMoves: Move[]): Set<number> {
  const topMoveIds = pokemon.often_used_move.slice(0, 10);
  return new Set(
    topMoveIds.filter((moveId) => {
      const move = allMoves.find((m) => m.id === moveId);
      return move != null && move.damage_class_number !== MOVE_DAMAGE_CLASS_STATUS;
    })
  );
}

const buildTopMoves = (moves: Move[], topMoveIds: number[]): Move[] => {
  const results: Move[] = [];
  const picked = new Set<number>();
  topMoveIds.forEach((moveId) => {
    const found = moves.find((move) => move.id === moveId);
    if (!found || picked.has(found.id)) {
      return;
    }
    results.push(found);
    picked.add(found.id);
  });
  return results;
};

const sortByKana = (a: Move, b: Move): number => a.name_ja.localeCompare(b.name_ja);

const sortMovesByDamageClass = (pokemon: PokemonV2, moves: Move[], topMoveIds: number[]): Move[] => {
  const topMoveIdSet = new Set(topMoveIds);
  const topMoves = buildTopMoves(moves, topMoveIds);
  const remainingMoves = moves.filter((move) => !topMoveIdSet.has(move.id));
  const physicalMoves = remainingMoves
    .filter((move) => move.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL)
    .sort(sortByKana);
  const specialMoves = remainingMoves
    .filter((move) => move.damage_class_number === MOVE_DAMAGE_CLASS_SPECIAL)
    .sort(sortByKana);
  const statusMoves = remainingMoves
    .filter((move) => move.damage_class_number === MOVE_DAMAGE_CLASS_STATUS)
    .sort(sortByKana);
  const isAttackFirst = pokemon.attack >= pokemon.special_attack;

  // TOP10の後に、攻撃傾向に合わせて物理/特殊を並べる
  if (isAttackFirst) {
    return [...topMoves, ...physicalMoves, ...specialMoves, ...statusMoves];
  }
  return [...topMoves, ...specialMoves, ...physicalMoves, ...statusMoves];
};

// 変化技を含めた技一覧の並び順を整形
export function getSortedMovesForAllSelect(
  pokemon: PokemonV2,
  moves: Move[]
): { sortedMoves: Move[]; topMoveIdSet: Set<number> } {
  const topMoveIds = pokemon.often_used_move.slice(0, 10);
  const sortedMoves = sortMovesByDamageClass(pokemon, moves, topMoveIds);
  return { sortedMoves, topMoveIdSet: new Set(topMoveIds) };
}

// 選択UI用の並び順（TOP10を先頭、その後その他の技）に整形
export function getSortedMovesForSelect(
  pokemon: PokemonV2,
  getAllAttackMoves: (p: PokemonV2) => Move[]
): { sortedMoves: Move[]; oftenUsedAttackMoveIds: Set<number> } {
  const allMoves = getAllAttackMoves(pokemon);
  const oftenUsedAttackMoveIds = getOftenUsedAttackMoveIds(pokemon, allMoves);
  const topMoveIds = pokemon.often_used_move.slice(0, 10);
  const sortedMoves = sortMovesByDamageClass(pokemon, allMoves, topMoveIds);
  return { sortedMoves, oftenUsedAttackMoveIds };
}
